import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { 
    Activity, 
    Mail, 
    Lock, 
    User, 
    Building2,
    Globe,
    Phone,
    Loader2, 
    AlertCircle, 
    CheckCircle2,
    Shield,
    Eye,
    EyeOff,
    Briefcase,
    UserCircle,
    ArrowRight,
    ArrowLeft,
    Sparkles,
    Search,
    ChevronDown,
    Check
} from 'lucide-react';
import { toast } from 'sonner';
import { countries } from '../data/countries';
import LanguageSelector from '../components/LanguageSelector';

const RegisterPage = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;
    const [accountType, setAccountType] = useState('individual');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        companyName: '',
        workEmail: '',
        country: '',
        countryCode: '',
        phone: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [countrySearch, setCountrySearch] = useState('');
    const [showCountryDropdown, setShowCountryDropdown] = useState(false);
    const countryDropdownRef = useRef(null);
    const countryInputRef = useRef(null);
    const { register, initiateGoogleLogin } = useAuth();
    const navigate = useNavigate();

    // Filter countries based on search
    const filteredCountries = countries.filter(country =>
        country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
        country.dial.includes(countrySearch)
    );

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
                setShowCountryDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleCountrySelect = (country) => {
        setFormData(prev => ({
            ...prev,
            country: country.name,
            countryCode: country.code,
            phone: prev.phone.startsWith('+') ? country.dial + ' ' : country.dial + ' ' + prev.phone
        }));
        setCountrySearch('');
        setShowCountryDropdown(false);
        setErrors(prev => ({ ...prev, country: '' }));
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.name.trim()) {
            newErrors.name = 'Full name is required';
        }
        
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email';
        }
        
        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        }
        
        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }
        
        if (accountType === 'laboratory') {
            if (!formData.companyName.trim()) {
                newErrors.companyName = 'Laboratory/Company name is required';
            }
            if (!formData.country.trim()) {
                newErrors.country = 'Country is required';
            }
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;
        
        setLoading(true);

        try {
            const registerData = {
                name: formData.name,
                email: formData.email,
                password: formData.password,
                account_type: accountType
            };
            
            if (accountType === 'laboratory') {
                registerData.company_name = formData.companyName;
                registerData.work_email = formData.workEmail || formData.email;
                registerData.country = formData.country;
                registerData.phone = formData.phone || null;
            }
            
            await register(
                registerData.email, 
                registerData.password, 
                registerData.name,
                registerData.account_type,
                registerData.company_name,
                registerData.work_email,
                registerData.country,
                registerData.phone
            );
            
            toast.success('Account created successfully!');
            navigate('/dashboard');
        } catch (err) {
            const message = err.response?.data?.detail || 'Registration failed. Please try again.';
            toast.error(message);
            setErrors({ submit: message });
        } finally {
            setLoading(false);
        }
    };

    const handleSocialLogin = (provider) => {
        if (provider === 'Google') {
            if (accountType === 'laboratory') {
                if (!formData.companyName.trim()) {
                    setErrors({ companyName: 'Please enter company name before continuing with Google' });
                    return;
                }
                if (!formData.country.trim()) {
                    setErrors({ country: 'Please select country before continuing with Google' });
                    return;
                }
            }
            initiateGoogleLogin(
                accountType,
                accountType === 'laboratory' ? formData.companyName : null,
                accountType === 'laboratory' ? formData.country : null
            );
        } else {
            toast.info(`${provider} sign-up coming soon!`);
        }
    };

    return (
        <div className="min-h-screen flex" data-testid="register-page">
            {/* Left Panel - Medical Background */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#1E3A5F] to-[#0D2137]">
                    <div className="absolute inset-0 opacity-10">
                        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                            <defs>
                                <pattern id="dna-pattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                                    <circle cx="10" cy="10" r="1.5" fill="currentColor" className="text-teal-400"/>
                                    <circle cx="5" cy="5" r="1" fill="currentColor" className="text-sky-400"/>
                                    <circle cx="15" cy="15" r="1" fill="currentColor" className="text-sky-400"/>
                                    <line x1="5" y1="5" x2="15" y2="15" stroke="currentColor" strokeWidth="0.3" className="text-teal-400"/>
                                </pattern>
                            </defs>
                            <rect width="100" height="100" fill="url(#dna-pattern)"/>
                        </svg>
                    </div>
                    <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-teal-500/20 rounded-full blur-3xl animate-pulse" />
                    <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
                </div>
                
                <div className="relative z-10 flex flex-col justify-between p-12 text-white">
                    <Link to="/" className="flex items-center gap-3 group">
                        <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center group-hover:bg-white/20 transition-all">
                            <Activity className="w-7 h-7 text-teal-400" />
                        </div>
                        <div>
                            <span className="text-xl font-bold">نتيجتي الطبية</span>
                            <span className="block text-xs text-sky-300">My Medical Results</span>
                        </div>
                    </Link>
                    
                    <div className="space-y-8">
                        <div>
                            <h1 className="text-4xl font-bold leading-tight mb-4">
                                AI-Powered Lab<br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-sky-400">
                                    Result Analysis
                                </span>
                            </h1>
                            <p className="text-lg text-slate-300 max-w-md leading-relaxed">
                                Join thousands of healthcare providers and individuals using AI to understand lab results better.
                            </p>
                        </div>
                        
                        <div className="space-y-4">
                            {[
                                { icon: Sparkles, text: 'AI-powered interpretations in seconds' },
                                { icon: Shield, text: 'HIPAA & GDPR compliant security' },
                                { icon: Activity, text: 'Track health trends over time' }
                            ].map((item, i) => (
                                <div key={i} className="flex items-center gap-3 text-slate-300">
                                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                                        <item.icon className="w-4 h-4 text-teal-400" />
                                    </div>
                                    <span>{item.text}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2 text-sm text-slate-400">
                            <Shield className="w-4 h-4" />
                            <span>256-bit SSL</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-400">
                            <CheckCircle2 className="w-4 h-4" />
                            <span>HIPAA Ready</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-400">
                            <Globe className="w-4 h-4" />
                            <span>GDPR Compliant</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Panel - Registration Form */}
            <div className="flex-1 flex items-center justify-center p-6 sm:p-8 lg:p-12 bg-gradient-to-br from-slate-50 via-white to-sky-50/30 overflow-y-auto">
                <div className="w-full max-w-lg">
                    {/* Mobile Logo */}
                    <div className="lg:hidden flex justify-center mb-8">
                        <Link to="/" className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1E3A5F] to-[#0D2137] flex items-center justify-center shadow-xl">
                                <Activity className="w-7 h-7 text-teal-400" />
                            </div>
                            <div>
                                <span className="text-xl font-bold text-slate-800">نتيجتي الطبية</span>
                                <span className="block text-xs text-slate-500">My Medical Results</span>
                            </div>
                        </Link>
                    </div>

                    <Card className="border-0 shadow-2xl shadow-slate-200/50 backdrop-blur-sm">
                        <CardContent className="p-8">
                            <div className="text-center mb-8">
                                <h2 className="text-2xl font-bold text-slate-800 mb-2">Create Your Account</h2>
                                <p className="text-slate-500">Choose your account type to get started</p>
                            </div>

                            {/* Account Type Selection */}
                            <div className="grid grid-cols-2 gap-4 mb-8" data-testid="account-type-selector">
                                <button
                                    type="button"
                                    onClick={() => setAccountType('individual')}
                                    className={`relative p-4 rounded-2xl border-2 transition-all text-left group ${
                                        accountType === 'individual'
                                            ? 'border-teal-500 bg-teal-50/50 shadow-lg shadow-teal-500/10'
                                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                    }`}
                                    data-testid="account-type-individual"
                                >
                                    {accountType === 'individual' && (
                                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                                            <CheckCircle2 className="w-4 h-4 text-white" />
                                        </div>
                                    )}
                                    <div className={`w-10 h-10 rounded-xl mb-3 flex items-center justify-center ${
                                        accountType === 'individual' ? 'bg-teal-500' : 'bg-slate-100 group-hover:bg-slate-200'
                                    }`}>
                                        <UserCircle className={`w-5 h-5 ${accountType === 'individual' ? 'text-white' : 'text-slate-500'}`} />
                                    </div>
                                    <h3 className={`font-semibold mb-1 ${accountType === 'individual' ? 'text-teal-700' : 'text-slate-700'}`}>
                                        Individual
                                    </h3>
                                    <p className="text-xs text-slate-500 leading-relaxed">
                                        For patients uploading personal lab results
                                    </p>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setAccountType('laboratory')}
                                    className={`relative p-4 rounded-2xl border-2 transition-all text-left group ${
                                        accountType === 'laboratory'
                                            ? 'border-[#1E3A5F] bg-[#1E3A5F]/5 shadow-lg shadow-[#1E3A5F]/10'
                                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                    }`}
                                    data-testid="account-type-laboratory"
                                >
                                    {accountType === 'laboratory' && (
                                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-[#1E3A5F] rounded-full flex items-center justify-center">
                                            <CheckCircle2 className="w-4 h-4 text-white" />
                                        </div>
                                    )}
                                    <div className={`w-10 h-10 rounded-xl mb-3 flex items-center justify-center ${
                                        accountType === 'laboratory' ? 'bg-[#1E3A5F]' : 'bg-slate-100 group-hover:bg-slate-200'
                                    }`}>
                                        <Building2 className={`w-5 h-5 ${accountType === 'laboratory' ? 'text-white' : 'text-slate-500'}`} />
                                    </div>
                                    <h3 className={`font-semibold mb-1 ${accountType === 'laboratory' ? 'text-[#1E3A5F]' : 'text-slate-700'}`}>
                                        Laboratory
                                    </h3>
                                    <p className="text-xs text-slate-500 leading-relaxed">
                                        For labs providing AI insights to patients
                                    </p>
                                </button>
                            </div>

                            {/* Registration Form */}
                            <form onSubmit={handleSubmit} className="space-y-5">
                                {errors.submit && (
                                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm" data-testid="register-error">
                                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                        {errors.submit}
                                    </div>
                                )}

                                {/* Basic Fields */}
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="name" className="text-slate-700">Full Name</Label>
                                        <div className="relative">
                                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <Input
                                                id="name"
                                                name="name"
                                                type="text"
                                                placeholder="John Doe"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className={`pl-10 h-12 rounded-xl border-slate-200 focus:border-teal-500 focus:ring-teal-500/20 ${
                                                    errors.name ? 'border-red-300 focus:border-red-500' : ''
                                                }`}
                                                data-testid="register-name-input"
                                            />
                                        </div>
                                        {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="email" className="text-slate-700">Email Address</Label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <Input
                                                id="email"
                                                name="email"
                                                type="email"
                                                placeholder="you@example.com"
                                                value={formData.email}
                                                onChange={handleChange}
                                                className={`pl-10 h-12 rounded-xl border-slate-200 focus:border-teal-500 focus:ring-teal-500/20 ${
                                                    errors.email ? 'border-red-300 focus:border-red-500' : ''
                                                }`}
                                                data-testid="register-email-input"
                                            />
                                        </div>
                                        {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="password" className="text-slate-700">Password</Label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <Input
                                                id="password"
                                                name="password"
                                                type={showPassword ? 'text' : 'password'}
                                                placeholder="••••••••"
                                                value={formData.password}
                                                onChange={handleChange}
                                                className={`pl-10 pr-10 h-12 rounded-xl border-slate-200 focus:border-teal-500 focus:ring-teal-500/20 ${
                                                    errors.password ? 'border-red-300 focus:border-red-500' : ''
                                                }`}
                                                data-testid="register-password-input"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                            >
                                                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                            </button>
                                        </div>
                                        {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
                                        <p className="text-xs text-slate-500">At least 8 characters</p>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="confirmPassword" className="text-slate-700">Confirm Password</Label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <Input
                                                id="confirmPassword"
                                                name="confirmPassword"
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                placeholder="••••••••"
                                                value={formData.confirmPassword}
                                                onChange={handleChange}
                                                className={`pl-10 pr-10 h-12 rounded-xl border-slate-200 focus:border-teal-500 focus:ring-teal-500/20 ${
                                                    errors.confirmPassword ? 'border-red-300 focus:border-red-500' : ''
                                                }`}
                                                data-testid="register-confirm-password-input"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                            >
                                                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                            </button>
                                        </div>
                                        {errors.confirmPassword && <p className="text-xs text-red-500">{errors.confirmPassword}</p>}
                                    </div>
                                </div>

                                {/* Laboratory-specific Fields */}
                                {accountType === 'laboratory' && (
                                    <div className="space-y-4 pt-4 border-t border-slate-100">
                                        <p className="text-sm font-medium text-[#1E3A5F] flex items-center gap-2">
                                            <Briefcase className="w-4 h-4" />
                                            Laboratory Information
                                        </p>
                                        
                                        <div className="space-y-2">
                                            <Label htmlFor="companyName" className="text-slate-700">Laboratory / Company Name *</Label>
                                            <div className="relative">
                                                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                                <Input
                                                    id="companyName"
                                                    name="companyName"
                                                    type="text"
                                                    placeholder="ABC Medical Labs"
                                                    value={formData.companyName}
                                                    onChange={handleChange}
                                                    className={`pl-10 h-12 rounded-xl border-slate-200 focus:border-[#1E3A5F] focus:ring-[#1E3A5F]/20 ${
                                                        errors.companyName ? 'border-red-300 focus:border-red-500' : ''
                                                    }`}
                                                    data-testid="register-company-name-input"
                                                />
                                            </div>
                                            {errors.companyName && <p className="text-xs text-red-500">{errors.companyName}</p>}
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="workEmail" className="text-slate-700">Work Email (optional)</Label>
                                            <div className="relative">
                                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                                <Input
                                                    id="workEmail"
                                                    name="workEmail"
                                                    type="email"
                                                    placeholder="contact@company.com"
                                                    value={formData.workEmail}
                                                    onChange={handleChange}
                                                    className="pl-10 h-12 rounded-xl border-slate-200 focus:border-[#1E3A5F] focus:ring-[#1E3A5F]/20"
                                                    data-testid="register-work-email-input"
                                                />
                                            </div>
                                        </div>

                                        {/* Searchable Country Dropdown */}
                                        <div className="space-y-2" ref={countryDropdownRef}>
                                            <Label htmlFor="country" className="text-slate-700">Country *</Label>
                                            <div className="relative">
                                                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 z-10" />
                                                <div 
                                                    className={`relative cursor-pointer`}
                                                    onClick={() => setShowCountryDropdown(true)}
                                                >
                                                    <Input
                                                        ref={countryInputRef}
                                                        id="country"
                                                        type="text"
                                                        placeholder="Search and select country..."
                                                        value={showCountryDropdown ? countrySearch : formData.country}
                                                        onChange={(e) => {
                                                            setCountrySearch(e.target.value);
                                                            if (!showCountryDropdown) setShowCountryDropdown(true);
                                                        }}
                                                        onFocus={() => setShowCountryDropdown(true)}
                                                        className={`pl-10 pr-10 h-12 rounded-xl border-slate-200 focus:border-[#1E3A5F] focus:ring-[#1E3A5F]/20 ${
                                                            errors.country ? 'border-red-300 focus:border-red-500' : ''
                                                        }`}
                                                        data-testid="register-country-input"
                                                        autoComplete="off"
                                                    />
                                                    <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`} />
                                                </div>
                                                
                                                {/* Dropdown */}
                                                {showCountryDropdown && (
                                                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                                                        {filteredCountries.length > 0 ? (
                                                            filteredCountries.map((country) => (
                                                                <div
                                                                    key={country.code}
                                                                    onClick={() => handleCountrySelect(country)}
                                                                    className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors ${
                                                                        formData.countryCode === country.code ? 'bg-teal-50' : ''
                                                                    }`}
                                                                    data-testid={`country-option-${country.code}`}
                                                                >
                                                                    <div className="flex items-center gap-3">
                                                                        <span className="text-lg">{country.code}</span>
                                                                        <span className="text-slate-700">{country.name}</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-slate-500 text-sm">{country.dial}</span>
                                                                        {formData.countryCode === country.code && (
                                                                            <Check className="w-4 h-4 text-teal-600" />
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="px-4 py-3 text-slate-500 text-center">
                                                                No countries found
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            {errors.country && <p className="text-xs text-red-500">{errors.country}</p>}
                                        </div>

                                        {/* Phone with auto-filled dial code */}
                                        <div className="space-y-2">
                                            <Label htmlFor="phone" className="text-slate-700">Phone Number (optional)</Label>
                                            <div className="relative">
                                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                                <Input
                                                    id="phone"
                                                    name="phone"
                                                    type="tel"
                                                    placeholder={formData.countryCode ? `${countries.find(c => c.code === formData.countryCode)?.dial || ''} 50 123 4567` : '+1 234 567 8900'}
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    className="pl-10 h-12 rounded-xl border-slate-200 focus:border-[#1E3A5F] focus:ring-[#1E3A5F]/20"
                                                    data-testid="register-phone-input"
                                                />
                                            </div>
                                            {formData.countryCode && (
                                                <p className="text-xs text-slate-500">
                                                    Country code {countries.find(c => c.code === formData.countryCode)?.dial} auto-applied
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Submit Button */}
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className={`w-full h-12 rounded-xl font-medium text-white shadow-lg transition-all hover:scale-[1.02] hover:-translate-y-0.5 ${
                                        accountType === 'laboratory'
                                            ? 'bg-[#1E3A5F] hover:bg-[#162d4a] shadow-[#1E3A5F]/25'
                                            : 'bg-teal-500 hover:bg-teal-600 shadow-teal-500/25'
                                    }`}
                                    data-testid="register-submit-btn"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                                            Creating Account...
                                        </>
                                    ) : (
                                        <>
                                            Create Account
                                            <ArrowRight className="ml-2 w-5 h-5" />
                                        </>
                                    )}
                                </Button>

                                {/* Social Sign-up */}
                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <span className="w-full border-t border-slate-200" />
                                    </div>
                                    <div className="relative flex justify-center text-xs uppercase">
                                        <span className="bg-white px-2 text-slate-500">Or continue with</span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => handleSocialLogin('Google')}
                                        className="h-12 rounded-xl border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                                        data-testid="google-signup-btn"
                                    >
                                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                        </svg>
                                        Google
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => handleSocialLogin('Microsoft')}
                                        className="h-12 rounded-xl border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                                        data-testid="microsoft-signup-btn"
                                    >
                                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                                            <path fill="#F25022" d="M1 1h10v10H1z"/>
                                            <path fill="#00A4EF" d="M1 13h10v10H1z"/>
                                            <path fill="#7FBA00" d="M13 1h10v10H13z"/>
                                            <path fill="#FFB900" d="M13 13h10v10H13z"/>
                                        </svg>
                                        Microsoft
                                    </Button>
                                </div>
                            </form>

                            {/* Trust indicators */}
                            <div className="mt-6 pt-6 border-t border-slate-100">
                                <div className="flex items-center justify-center gap-4 text-xs text-slate-500 mb-4">
                                    <div className="flex items-center gap-1">
                                        <Shield className="w-3 h-3" />
                                        <span>Encrypted</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <CheckCircle2 className="w-3 h-3" />
                                        <span>HIPAA</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Globe className="w-3 h-3" />
                                        <span>GDPR</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Lock className="w-3 h-3" />
                                        <span>SSL</span>
                                    </div>
                                </div>
                                <p className="text-xs text-slate-400 text-center leading-relaxed">
                                    This platform provides AI-driven educational insights and does not replace professional medical diagnosis.
                                </p>
                            </div>

                            {/* Sign in link */}
                            <div className="mt-6 text-center">
                                <p className="text-slate-600">
                                    Already have an account?{' '}
                                    <Link to="/login" className="text-teal-600 font-medium hover:underline" data-testid="login-link">
                                        Sign in
                                    </Link>
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default RegisterPage;
